import React, { useState, useCallback, useMemo, useEffect } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Users,
  Briefcase,
  Info,
  Check,
  Sparkles,
  DollarSign,
  Zap,
  Gauge,
  Wrench,
  User,
  ChevronDown,
  ChevronUp,
  X,
  ArrowLeft,
} from "lucide-react";
import { PageHeader } from "@shared/components/PageHeader";
import { useNavigate, useParams } from "react-router-dom";
import { useGetChoices } from "@features/ux/components/shared/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useCreateSegment,
  useGetAllFilterFields,
  useGetCatSubcatChoices,
  useGetPlanChoices,
  useGetSegment,
  useUpdateSegment,
} from "../../../hooks";
import {
  useGetAllDepartments,
  useGetAllRoles,
} from "@features/ux/components/recipientList/hooks";

// Icon mapping from API icon names to lucide-react components
const iconMapping: Record<string, any> = {
  receipt: DollarSign,
  trending_up: Zap,
  speed: Gauge,
  support_agent: Wrench,
  person_pin: User,
};

export default function SegmentCreation() {
  const navigate = useNavigate();
  const { action, id } = useParams();
  const remoteUtilityId = useRemoteUtilityId();
  const isEditMode = action === "edit" && id;

  const [audienceType, setAudienceType] = useState(1);
  const [selectDepartment, setSelectDepartment] = useState("");
  const [formData, setFormData] = useState({
    segmentName: "",
    description: "",
    department: "",
    role: "",
    accountStatus: "all",
  });

  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [selectCategory, setSeletCategory] = useState("");
  const [selectSubCategory, setSeletSubCategory] = useState("");

  const { data: segment } = useGetSegment(
    id,
    undefined,
    isEditMode ? "edit" : undefined
  );

  const create = useCreateSegment();
  const update = useUpdateSegment(id);

  const { data: filterData } = useGetAllFilterFields();
  const { data: departmentList } = useGetAllDepartments();
  const { data: roleList } = useGetAllRoles(selectDepartment);
  const departmentOptions = useMemo(
    () =>
      departmentList?.result?.map((item) => ({
        value: item.code,
        label: item.name,
      })) || [],
    [departmentList]
  );

  const roleOptions = useMemo(
    () =>
      roleList?.result?.flatMap(
        (item) =>
          item.roles?.map((role) => ({
            value: role.id,
            label: role.roleName,
          })) || []
      ) || [],
    [roleList]
  );

  console.log(roleOptions);

  const { data: getAudienceType } = useGetChoices(
    "audience_type",
    "quick_messaging"
  );
  const { data: getBillGenerated } = useGetChoices("billing_bill_generated");
  const { data: getOverdueCondition } = useGetChoices("operators");
  const { data: getAbnormalPeriod } = useGetChoices(
    "consumption_abnormal_period"
  );
  const { data: getUsageCondition } = useGetChoices("operators");
  const { data: getMeterCondition } = useGetChoices("metering_meter_condition");
  const { data: getMeterStatus } = useGetChoices("metering_meter_status");
  const { data: getRequestStatus } = useGetChoices("service_request_status");
  const { data: getTechnicianVisit } = useGetChoices(
    "service_technician_visit"
  );
  const { data: getComplaintPeriod } = useGetChoices(
    "service_complaint_period"
  );
  const { data: getConsumerCategory } =
    useGetCatSubcatChoices("consumer_category");
  const { data: getConsumerSubCategory } = useGetCatSubcatChoices(
    "consumer_sub_category",
    selectCategory
  );
  const { data: getPlanType } = useGetPlanChoices(
    "plans",
    selectCategory,
    selectSubCategory
  );
  const { data: getCustomerStatus } = useGetChoices("profile_customer_status");
  const { data: getLocationType } = useGetChoices("profile_location_type");
  const { data: getTariffPlan } = useGetChoices("tariff_plan");
  const { data: getDepartment } = useGetChoices("department");
  const { data: getRole } = useGetChoices("role");

  const mapChoices = (data: any) =>
    data?.result?.map((item: any) => ({
      value: item.key,
      label: item.value,
    })) || [];

  const audienceTypeOptions = useMemo(
    () => mapChoices(getAudienceType),
    [getAudienceType]
  );

  const billGeneratedOptions = useMemo(
    () => mapChoices(getBillGenerated),
    [getBillGenerated]
  );

  const overdueConditionOptions = useMemo(
    () => mapChoices(getOverdueCondition),
    [getOverdueCondition]
  );

  const abnormalPeriodOptions = useMemo(
    () => mapChoices(getAbnormalPeriod),
    [getAbnormalPeriod]
  );

  const usageConditionOptions = useMemo(
    () => mapChoices(getUsageCondition),
    [getUsageCondition]
  );

  const meterConditionOptions = useMemo(
    () => mapChoices(getMeterCondition),
    [getMeterCondition]
  );

  const meterStatusOptions = useMemo(
    () => mapChoices(getMeterStatus),
    [getMeterStatus]
  );

  const requestStatusOptions = useMemo(
    () => mapChoices(getRequestStatus),
    [getRequestStatus]
  );

  const technicianVisitOptions = useMemo(
    () => mapChoices(getTechnicianVisit),
    [getTechnicianVisit]
  );

  const complaintPeriodOptions = useMemo(
    () => mapChoices(getComplaintPeriod),
    [getComplaintPeriod]
  );

  const consumerCategoryOptions = useMemo(
    () => mapChoices(getConsumerCategory),
    [getConsumerCategory]
  );

  const consumerSubCategoryOptions = useMemo(
    () => mapChoices(getConsumerSubCategory),
    [getConsumerSubCategory]
  );

  const planTypeOptions = useMemo(() => mapChoices(getPlanType), [getPlanType]);

  const customerStatusOptions = useMemo(
    () => mapChoices(getCustomerStatus),
    [getCustomerStatus]
  );

  const locationTypeOptions = useMemo(
    () => mapChoices(getLocationType),
    [getLocationType]
  );

  const tariffPlanOptions = useMemo(
    () => mapChoices(getTariffPlan),
    [getTariffPlan]
  );

  // API field name to form field name mapping
  const apiToFormFieldMapping: Record<string, string> = {
    billGenerated: "billing_billGenerated",
    billingPeriodFrom: "billing_periodFrom",
    billingPeriodTo: "billing_periodTo",
    preDueReminderDays: "billing_preDueReminder",
    overdueDaysCondition: "billing_overdueCondition",
    overdueDaysValue: "billing_overdueValue",
    outstandingBalanceMin: "billing_outstandingMin",
    outstandingBalanceMax: "billing_outstandingMax",
    lastBillDate: "billing_lastBillDate",
    lastPaymentDate: "billing_lastPaymentDate",
    highConsumption: "consumption_highConsumption",
    lowOrZeroConsumption: "consumption_lowConsumption",
    abnormalConsumptionPeriod: "consumption_abnormalPeriod",
    abnormalConsumptionDeviation: "consumption_abnormalDeviation",
    usageThresholdCondition: "consumption_usageCondition",
    usageThresholdValue: "consumption_usageValue",
    meterReadingDueDays: "metering_readingDue",
    noAccessConsumersRcnt: "metering_noAccess",
    consecutiveEstimatedReadings: "metering_estimatedReading",
    meterCondition: "metering_condition",
    meterStatus: "metering_status",
    serviceRequestStatus: "service_requestStatus",
    technicianVisitScheduled: "service_technicianVisit",
    hasOpenServiceRequest: "service_openRequest",
    hasOpenComplaint: "service_openComplaint",
    highComplaintCount: "service_highComplaintCount",
    highComplaintPeriod: "service_highComplaintPeriod",
    feedbackAfterClosureDays: "service_feedbackAfterClosure",
    consumerCategory: "profile_consumerCategory",
    consumerSubCategory: "profile_consumerSubCategory",
    planType: "profile_plan",
    customerStatus: "profile_customerStatus",
    newConsumersJoinedWithinDays: "profile_newConsumers",
    locationType: "profile_locationType",
    locationValue: "profile_locationValue",
    tariffPlan: "profile_tariffPlan",
  };

  // Reverse mapping for payload generation
  const formToApiFieldMapping: Record<string, string> = useMemo(() => {
    const reverseMap: Record<string, string> = {};
    Object.entries(apiToFormFieldMapping).forEach(([apiField, formField]) => {
      reverseMap[formField] = apiField;
    });
    return reverseMap;
  }, []);

  // Get category prefix from form field name
  const getCategoryPrefix = (formFieldName: string): string => {
    const prefixes = [
      "billing_",
      "consumption_",
      "metering_",
      "service_",
      "profile_",
    ];
    return prefixes.find((prefix) => formFieldName.startsWith(prefix)) || "";
  };

  // Map API category names to frontend prefixes
  const categoryToPrefix: Record<string, string> = {
    billing: "billing_",
    consumption: "consumption_",
    metering: "metering_",
    service_support: "service_",
    profile_location: "profile_",
  };

  // Map API category names to color schemes
  const categoryColors: Record<string, string> = {
    billing: "emerald",
    consumption: "blue",
    metering: "purple",
    service_support: "orange",
    profile_location: "indigo",
  };

  // Generate filter categories from API response
  const filterCategories = useMemo(() => {
    if (!filterData) return [];

    return filterData.map((category: any) => {
      const prefix = categoryToPrefix[category.category] || "";
      const color = categoryColors[category.category] || "gray";
      const IconComponent = iconMapping[category.icon] || User;

      return {
        id: category.category,
        title: category.categoryDisplayName,
        icon: IconComponent,
        color: color,
        prefix: prefix,
        description: category.description,
      };
    });
  }, [filterData]);

  const colorClasses: Record<string, any> = {
    emerald: {
      bg: "bg-emerald-50",
      text: "text-emerald-600",
      iconBg: "bg-emerald-100",
    },
    blue: {
      bg: "bg-blue-50",
      text: "text-blue-600",
      iconBg: "bg-blue-100",
    },
    purple: {
      bg: "bg-purple-50",
      text: "text-purple-600",
      iconBg: "bg-purple-100",
    },
    orange: {
      bg: "bg-orange-50",
      text: "text-orange-600",
      iconBg: "bg-orange-100",
    },
    indigo: {
      bg: "bg-indigo-50",
      text: "text-indigo-600",
      iconBg: "bg-indigo-100",
    },
    gray: {
      bg: "bg-gray-50",
      text: "text-gray-600",
      iconBg: "bg-gray-100",
    },
  };

  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "segmentName",
        type: "text",
        label: "Segment Name",
        placeholder: "e.g., High Value Customers",
        required: true,
        className: "col-span-2",
      },
      {
        name: "description",
        type: "textarea",
        label: "Description",
        placeholder: "Describe the purpose of this segment...",
        rows: 3,
        required: false,
        className: "col-span-2",
      },
    ],
    []
  );

  const businessUserFields: FormField[] = useMemo(
    () => [
      {
        name: "department",
        type: "select",
        label: "Department",
        placeholder: "All Departments",
        options: departmentOptions,
        required: false,
        group: "Filter Criteria",
        groupColorNeeded: false,
        className: "col-span-1",
      },
      {
        name: "role",
        type: "select",
        label: "Role",
        placeholder: "All Roles",
        options: roleOptions,
        required: false,
        group: "Filter Criteria",
        groupColorNeeded: false,
        className: "col-span-1",
      },
      {
        name: "accountStatus",
        type: "radio",
        label: "Account Status",
        options: [
          { label: "All User", value: 1 },
          { label: "Active User Only", value: 2 },
          { label: "Inactive User Only", value: 3 },
        ],
        required: false,
        group: "Filter Criteria",
        groupColorNeeded: false,
        className: "col-span-2",
      },
    ],
    [departmentOptions, roleOptions]
  );

  const allFilterFields: FormField[] = useMemo(() => {
    const createShowWhen = (prefix: string) => () =>
      expandedCategories.includes(prefix);

    return [
      // Billing Filters
      {
        name: "billing_billGenerated",
        type: "select",
        label: "Bill Generated",
        placeholder: "Select option",
        showWhen: createShowWhen("billing_"),
        options: billGeneratedOptions,
        className: "col-span-2",
      },
      {
        name: "billing_periodFrom",
        type: "datepicker",
        label: "From Date",
        showWhen: (formValues: any) =>
          expandedCategories.includes("billing_") &&
          formValues.billing_billGenerated === "3",
        className: "col-span-1",
      },
      {
        name: "billing_periodTo",
        type: "datepicker",
        label: "To Date",
        showWhen: (formValues: any) =>
          expandedCategories.includes("billing_") &&
          formValues.billing_billGenerated === "3",
        className: "col-span-1",
      },

      {
        name: "billing_preDueReminder",
        type: "number",
        label: "Pre-Due Reminder (days before due date)",
        placeholder: "e.g., 7",
        showWhen: createShowWhen("billing_"),
        className: "col-span-2",
      },
      {
        name: "billing_overdueCondition",
        type: "select",
        label: "Overdue Days - Condition",
        placeholder: "Select condition",
        showWhen: createShowWhen("billing_"),
        options: overdueConditionOptions,
        className: "col-span-1",
      },
      {
        name: "billing_overdueValue",
        type: "number",
        label: "Overdue Days - Value",
        placeholder: "Days",
        showWhen: createShowWhen("billing_"),
        className: "col-span-1",
      },
      {
        name: "billing_outstandingMin",
        type: "number",
        label: "Outstanding Balance - Min Amount",
        placeholder: "Min Amount",
        showWhen: createShowWhen("billing_"),
        className: "col-span-1",
      },
      {
        name: "billing_outstandingMax",
        type: "number",
        label: "Outstanding Balance - Max Amount",
        placeholder: "Max Amount",
        showWhen: createShowWhen("billing_"),
        className: "col-span-1",
      },
      {
        name: "billing_lastBillDate",
        type: "datepicker",
        label: "Last Bill Date",
        showWhen: createShowWhen("billing_"),
        className: "col-span-1",
      },
      {
        name: "billing_lastPaymentDate",
        type: "datepicker",
        label: "Last Payment Date",
        showWhen: createShowWhen("billing_"),
        className: "col-span-1",
      },
      // Consumption Filters
      {
        name: "consumption_highConsumption",
        type: "number",
        label: "High Consumption (exceeds plan average)",
        placeholder: "Threshold %",
        showWhen: createShowWhen("consumption_"),
        className: "col-span-2",
      },
      {
        name: "consumption_lowConsumption",
        type: "number",
        label: "Low or Zero Consumption",
        placeholder: "Below threshold",
        showWhen: createShowWhen("consumption_"),
        className: "col-span-2",
      },
      {
        name: "consumption_abnormalPeriod",
        type: "select",
        label: "Abnormal Consumption - Period",
        placeholder: "Select period",
        showWhen: createShowWhen("consumption_"),
        options: abnormalPeriodOptions,
        className: "col-span-1",
      },
      {
        name: "consumption_abnormalDeviation",
        type: "number",
        label: "Abnormal Consumption - Deviation %",
        placeholder: "Deviation %",
        showWhen: createShowWhen("consumption_"),
        className: "col-span-1",
      },
      {
        name: "consumption_usageCondition",
        type: "select",
        label: "Usage Threshold - Condition",
        placeholder: "Select condition",
        showWhen: createShowWhen("consumption_"),
        options: usageConditionOptions,
        className: "col-span-1",
      },
      {
        name: "consumption_usageValue",
        type: "number",
        label: "Usage Threshold - Value",
        placeholder: "Value",
        showWhen: createShowWhen("consumption_"),
        className: "col-span-1",
      },
      // Metering Filters
      {
        name: "metering_readingDue",
        type: "number",
        label: "Meter Reading Due (days)",
        placeholder: "Days until due",
        showWhen: createShowWhen("metering_"),
        className: "col-span-2",
      },
      {
        name: "metering_condition",
        type: "multi-select",
        label: "Meter Condition",
        placeholder: "Select condition",
        showWhen: createShowWhen("metering_"),
        options: meterConditionOptions,
        className: "col-span-1",
      },
      {
        name: "metering_status",
        type: "select",
        label: "Meter Status",
        placeholder: "Select status",
        showWhen: createShowWhen("metering_"),
        options: meterStatusOptions,
        className: "col-span-1",
      },
      {
        name: "metering_noAccess",
        type: "checkbox",
        label: "No Access Consumers (RCNT)",
        showWhen: createShowWhen("metering_"),
        className: "col-span-2",
        value: false,
      },

      {
        name: "metering_estimatedReading",
        type: "number",
        label: "Consecutive Estimated Readings",
        placeholder: "Number of consecutive readings",
        showWhen: createShowWhen("metering_"),
        className: "col-span-2",
      },
      // Service & Support Filters
      {
        name: "service_requestStatus",
        type: "multi-select",
        label: "Service Request Status",
        showWhen: createShowWhen("service_"),
        options: requestStatusOptions,
        className: "col-span-2",
      },
      {
        name: "service_technicianVisit",
        type: "select",
        label: "Technician Visit Scheduled",
        placeholder: "Select timeframe",
        showWhen: createShowWhen("service_"),
        options: technicianVisitOptions,
        className: "col-span-2",
      },
      {
        name: "service_openRequest",
        type: "checkbox",
        label: "Has Open Service Request",
        showWhen: createShowWhen("service_"),
        className: "col-span-2",
        value: false,
      },
      {
        name: "service_openComplaint",
        type: "checkbox",
        label: "Has Open Complaint",
        showWhen: createShowWhen("service_"),
        className: "col-span-2",
        value: false,
      },
      {
        name: "service_highComplaintCount",
        type: "number",
        label: "High Complaint - Number of Complaints",
        placeholder: "Number of complaints",
        showWhen: createShowWhen("service_"),
        className: "col-span-1",
      },
      {
        name: "service_highComplaintPeriod",
        type: "select",
        label: "High Complaint - Period",
        placeholder: "Select period",
        showWhen: createShowWhen("service_"),
        options: complaintPeriodOptions,
        className: "col-span-1",
      },
      {
        name: "service_feedbackAfterClosure",
        type: "number",
        label: "Feedback After Closure (days)",
        placeholder: "Days after closure",
        showWhen: createShowWhen("service_"),
        className: "col-span-2",
      },
      // Customer Profile & Location Filters
      {
        name: "profile_consumerCategory",
        type: "select",
        label: "Consumer Category",
        showWhen: createShowWhen("profile_"),
        options: consumerCategoryOptions,
        className: "col-span-2",
      },
      {
        name: "profile_consumerSubCategory",
        type: "select",
        label: "Consumer Sub Category",
        placeholder: "Select sub category",
        showWhen: createShowWhen("profile_"),
        options: consumerSubCategoryOptions,
        className: "col-span-2",
      },
      {
        name: "profile_plan",
        type: "select",
        label: "Plan Type",
        placeholder: "Select plan",
        showWhen: createShowWhen("profile_"),
        options: planTypeOptions,
        className: "col-span-2",
      },
      {
        name: "profile_customerStatus",
        type: "select",
        label: "Customer Status",
        showWhen: createShowWhen("profile_"),
        options: customerStatusOptions,
        className: "col-span-2",
      },
      {
        name: "profile_newConsumers",
        type: "number",
        label: "New Consumers (joined within days)",
        placeholder: "Number of days",
        showWhen: createShowWhen("profile_"),
        className: "col-span-2",
      },
      {
        name: "profile_locationType",
        type: "select",
        label: "Location Type",
        placeholder: "Select location type",
        showWhen: createShowWhen("profile_"),
        options: locationTypeOptions,
        className: "col-span-1",
      },
      {
        name: "profile_locationValue",
        type: "text",
        label: "Location Value",
        placeholder: "Enter location",
        showWhen: createShowWhen("profile_"),
        className: "col-span-1",
      },
      {
        name: "profile_tariffPlan",
        type: "select",
        label: "Tariff Plan",
        placeholder: "Select tariff plan",
        showWhen: createShowWhen("profile_"),
        options: tariffPlanOptions,
        className: "col-span-2",
      },
    ];
  }, [
    expandedCategories,
    billGeneratedOptions,
    overdueConditionOptions,
    abnormalPeriodOptions,
    usageConditionOptions,
    meterConditionOptions,
    meterStatusOptions,
    requestStatusOptions,
    technicianVisitOptions,
    complaintPeriodOptions,
    consumerCategoryOptions,
    consumerSubCategoryOptions,
    planTypeOptions,
    customerStatusOptions,
    locationTypeOptions,
    tariffPlanOptions,
  ]);

  const mainForm = FormService.createForm({
    fields:
      audienceType === 2 ? [...formFields, ...businessUserFields] : formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  const filterForm = FormService.createForm({
    fields: allFilterFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: filterValues,
  });

  useEffect(() => {
    if (isEditMode && segment?.result) {
      const segmentData = segment.result;

      setAudienceType(segmentData.audienceType || 1);

      const mainInitialValues = {
        segmentName: segmentData.name || "",
        description: segmentData.description || "",
        department: "",
        role: "",
        accountStatus: "all",
      };

      const newFilterValues: Record<string, any> = {};
      const categoriesToExpand: string[] = [];

      if (segmentData.criteria && Array.isArray(segmentData.criteria)) {
        segmentData.criteria.forEach((criteriaItem: any) => {
          if (criteriaItem.fields && typeof criteriaItem.fields === "object") {
            Object.entries(criteriaItem.fields).forEach(
              ([apiFieldName, value]) => {
                const formFieldName = apiToFormFieldMapping[apiFieldName];

                if (formFieldName) {
                  newFilterValues[formFieldName] = value;

                  const categoryPrefix = getCategoryPrefix(formFieldName);
                  if (
                    categoryPrefix &&
                    !categoriesToExpand.includes(categoryPrefix)
                  ) {
                    categoriesToExpand.push(categoryPrefix);
                  }
                }
              }
            );
          }

          if (
            segmentData.audienceType === 2 &&
            criteriaItem.category === "Business User Filter"
          ) {
            if (criteriaItem.fields) {
              mainInitialValues.department =
                criteriaItem.fields.department || "";
              mainInitialValues.role = criteriaItem.fields.role || "";
              mainInitialValues.accountStatus =
                criteriaItem.fields.accountStatus || "all";
            }
          }
        });
      }

      setFormData(mainInitialValues);
      setFilterValues(newFilterValues);
      setExpandedCategories(categoriesToExpand);

      FormService.populateForm(
        mainForm,
        mainInitialValues,
        audienceType === 2 ? [...formFields, ...businessUserFields] : formFields
      );
      FormService.populateForm(filterForm, newFilterValues, allFilterFields);
    }
  }, [isEditMode, segment, audienceType]);

  const handleFieldChange = useCallback((field: string, value: any) => {
    switch (field) {
      case "department":
        setSelectDepartment(value);
        break;
    }
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleFilterChange = useCallback((field: string, value: any) => {
    switch (field) {
      case "profile_consumerCategory":
        setSeletCategory(value);
        break;
      case "profile_consumerSubCategory":
        setSeletSubCategory(value);
        break;
    }
    setFilterValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleCancel = () => {
    mainForm.reset();
    filterForm.reset();
    navigate("/communication-hub/settings");
    setFilterValues({});
    setExpandedCategories([]);
    setAudienceType(1);
  };

  const handleSubmit = useCallback(async () => {
    const mainValues = mainForm.getValues();
    const operation = isEditMode ? "update" : "create";

    const criteria: any[] = [];

    switch (audienceType) {
      case 1: {
        // Build category map using API category names from filterData
        const categoryMap: Record<
          string,
          {
            apiCategory: string;
            title: string;
            displayName: string;
            fields: Record<string, any>;
          }
        > = {};

        // Initialize category map from API response
        if (filterData) {
          filterData.forEach((apiCategory: any) => {
            const prefix = categoryToPrefix[apiCategory.category];
            if (prefix) {
              categoryMap[prefix] = {
                apiCategory: apiCategory.category,
                title: apiCategory.category_display_name,
                displayName: apiCategory.categoryDisplayName,
                fields: {},
              };
            }
          });
        }

        // Populate fields with converted API field names
        Object.keys(filterValues).forEach((formFieldKey) => {
          const value = filterValues[formFieldKey];
          if (value === null || value === undefined || value === "") return;
          if (Array.isArray(value) && value.length === 0) return;

          const categoryPrefix = getCategoryPrefix(formFieldKey);
          const apiFieldName = formToApiFieldMapping[formFieldKey];

          if (categoryPrefix && categoryMap[categoryPrefix] && apiFieldName) {
            categoryMap[categoryPrefix].fields[apiFieldName] = value;
          }
        });

        // Build criteria array with filter_name
        Object.keys(categoryMap).forEach((prefix) => {
          const category = categoryMap[prefix];
          if (Object.keys(category.fields).length > 0) {
            criteria.push({
              category: category.title,
              filter_name: category.displayName, // Added filter_name
              fields: category.fields,
            });
          }
        });

        break;
      }

      case 2: {
        const businessCategory = {
          category: "Business User Filter",
          filter_name: "Business User Filter", // Added filter_name
          fields: {
            department: mainValues.department || "",
            role: mainValues.role || "",
            account_status: mainValues.accountStatus || "",
          },
        };

        criteria.push(businessCategory);

        break;
      }
      default:
        break;
    }

    switch (operation) {
      case "update":
        const updatePayload = {
          id: id,
          remote_utility_id: remoteUtilityId,
          name: mainValues.segmentName,
          description: mainValues.description,
          audience_type_id: audienceType,
          criteria,
          status: "active",
        };

        try {
          await update.mutateAsync(updatePayload);
          handleCancel();
        } catch (error) {}
        break;

      case "create":
        const createPayload = {
          remote_utility_id: remoteUtilityId,
          name: mainValues.segmentName,
          description: mainValues.description,
          audience_type_id: audienceType,
          criteria,
          status: "active",
        };

        try {
          await create.mutateAsync(createPayload);
          handleCancel();
        } catch (error) {}
        break;
    }
  }, [
    audienceType,
    mainForm,
    filterValues,
    remoteUtilityId,
    isEditMode,
    id,
    filterData,
    formToApiFieldMapping,
  ]);

  const getActiveFilterCount = () => {
    return Object.keys(filterValues).filter((key) => {
      const value = filterValues[key];
      if (value === null || value === undefined || value === "") return false;
      if (Array.isArray(value)) return value.length > 0;
      return true;
    }).length;
  };

  const getActiveFiltersForCategory = (prefix: string) => {
    return Object.keys(filterValues).filter((key) => {
      if (!key.startsWith(prefix)) return false;
      const value = filterValues[key];
      if (value === null || value === undefined || value === "") return false;
      if (Array.isArray(value)) return value.length > 0;
      return true;
    });
  };

  const removeFilterField = (fieldName: string) => {
    setFilterValues((prev) => {
      const newFilters = { ...prev };
      delete newFilters[fieldName];
      return newFilters;
    });
  };

  const getFieldLabel = (fieldName: string) => {
    const field = allFilterFields.find((f) => f.name === fieldName);
    return field?.label || fieldName;
  };

  return (
    <div>
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <ArrowLeft
            className="w-5 h-5 cursor-pointer text-gray-700"
            onClick={() => navigate("/communication-hub/settings")}
          />

          <div className="flex flex-col">
            <PageHeader
              title={isEditMode ? "Edit Segment" : "Create New Segment"}
              subtitle={
                isEditMode
                  ? "Update your segment configuration"
                  : "Define a reusable audience segment"
              }
            />
          </div>
        </div>
      </div>

      <div className="p-1">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">1</span>
                </div>
                <h2 className="text-lg font-bold text-gray-900">
                  Audience Type
                </h2>
                <span className="text-red-500">*</span>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {audienceTypeOptions?.map((opt) => {
                  const isSelected = opt.value === audienceType;

                  let uiConfig;
                  switch (opt.value) {
                    case 1:
                      uiConfig = {
                        colorBorder: "border-blue-500",
                        colorBg: "bg-blue-50",
                        colorIcon: "bg-blue-600",
                        checkColor: "text-blue-600",
                        icon: Users,
                        description: "Target your customer base",
                      };
                      break;
                    case 2:
                      uiConfig = {
                        colorBorder: "border-purple-500",
                        colorBg: "bg-purple-50",
                        colorIcon: "bg-purple-600",
                        checkColor: "text-purple-600",
                        icon: Briefcase,
                        description: "Target internal users",
                      };
                      break;
                    default:
                      uiConfig = {
                        colorBorder: "border-gray-500",
                        colorBg: "bg-gray-50",
                        colorIcon: "bg-gray-600",
                        checkColor: "text-gray-600",
                        icon: Users,
                        description: "Default audience",
                      };
                  }

                  const IconComponent = uiConfig.icon;

                  return (
                    <button
                      key={opt.value}
                      onClick={() => setAudienceType(opt.value)}
                      className={`p-5 rounded-xl border-2 transition-all text-left ${
                        isSelected
                          ? `${uiConfig.colorBorder} ${uiConfig.colorBg}`
                          : "border-gray-300 bg-white hover:border-gray-400"
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div
                          className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            isSelected ? uiConfig.colorIcon : "bg-gray-200"
                          }`}
                        >
                          <IconComponent
                            className={`w-6 h-6 ${
                              isSelected ? "text-white" : "text-gray-500"
                            }`}
                          />
                        </div>

                        {isSelected && (
                          <Check className={`w-5 h-5 ${uiConfig.checkColor}`} />
                        )}
                      </div>

                      <h3 className="font-semibold text-gray-900 mb-1">
                        {opt.label}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {uiConfig.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">2</span>
                </div>
                <h2 className="text-lg font-bold text-gray-900">
                  Segment Details
                </h2>
              </div>

              <DynamicForm
                fields={
                  audienceType === 2
                    ? [...formFields, ...businessUserFields]
                    : formFields
                }
                form={mainForm}
                gridColumns={2}
                responsiveColumns={{ sm: 1, md: 2 }}
                onSubmit={handleSubmit}
                onReset={handleCancel}
                onFieldChange={handleFieldChange}
                config={{
                  layout: "grid",
                  columns: 2,
                  showSubmitButton: false,
                  showResetButton: false,
                  spacing: "medium",
                  classes: {
                    container: "w-full",
                    label: "block text-sm font-semibold text-gray-700 mb-2",
                    input:
                      "w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all",
                    select:
                      "w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all",
                    textarea:
                      "w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none transition-all",
                    error: "text-red-600 text-sm mt-1",
                    helperText: "text-xs text-gray-500 mt-1",
                  },
                }}
              />
            </div>

            {audienceType === 1 && (
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center">
                      <span className="text-white font-bold text-sm">3</span>
                    </div>
                    <h2 className="text-lg font-bold text-gray-900">
                      Filter Criteria
                    </h2>
                  </div>
                  {getActiveFilterCount() > 0 && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                      {getActiveFilterCount()} active
                    </span>
                  )}
                </div>

                <p className="text-sm text-gray-600 mb-5">
                  Configure filters to automatically segment your customers
                </p>

                <div className="space-y-3">
                  {filterCategories.map((category: any) => {
                    const Icon = category.icon;
                    const colors = colorClasses[category.color];
                    const isExpanded = expandedCategories.includes(
                      category.prefix
                    );
                    const categoryFilterCount = getActiveFiltersForCategory(
                      category.prefix
                    ).length;
                    const categoryFieldCount = allFilterFields.filter((f) =>
                      f.name.startsWith(category.prefix)
                    ).length;

                    return (
                      <div
                        key={category.id}
                        className="border-2 border-gray-200 rounded-xl overflow-hidden transition-all bg-white"
                      >
                        <button
                          onClick={() => {
                            setExpandedCategories((prev) =>
                              isExpanded
                                ? prev.filter((c) => c !== category.prefix)
                                : [...prev, category.prefix]
                            );
                          }}
                          className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.iconBg}`}
                            >
                              <Icon className={`w-5 h-5 ${colors.text}`} />
                            </div>
                            <div className="text-left">
                              <h3 className="font-semibold text-gray-900">
                                {category.title}
                              </h3>
                              <p className="text-xs text-gray-500">
                                {categoryFieldCount} available filters
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {categoryFilterCount > 0 && (
                              <span
                                className={`px-2.5 py-1 ${colors.bg} ${colors.text} rounded-full text-xs font-semibold`}
                              >
                                {categoryFilterCount}
                              </span>
                            )}
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </button>

                        {isExpanded && (
                          <div className="px-5 py-4 bg-gray-50 border-t-2 border-gray-200">
                            <DynamicForm
                              fields={allFilterFields.filter((f) =>
                                f.name.startsWith(category.prefix)
                              )}
                              form={filterForm}
                              gridColumns={2}
                              responsiveColumns={{ sm: 1, md: 2 }}
                              onSubmit={() => {}}
                              onFieldChange={handleFilterChange}
                              config={{
                                layout: "grid",
                                columns: 2,
                                showSubmitButton: false,
                                showResetButton: false,
                                spacing: "medium",
                                classes: {
                                  container: "w-full",
                                  label:
                                    "block text-sm font-medium text-gray-900 mb-2",
                                  input:
                                    "w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm",
                                  select:
                                    "w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm",
                                  textarea:
                                    "w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none transition-all text-sm",
                                  checkbox:
                                    "w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500",
                                  error: "text-red-600 text-sm mt-1",
                                  helperText: "text-xs text-gray-500 mt-1",
                                },
                              }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
              <div className="flex items-center justify-end gap-3">
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button onClick={handleSubmit}>
                  {isEditMode ? "Update Segment" : "Create Segment"}
                </Button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-5">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                  <h3 className="font-bold text-gray-900">Summary</h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                      Audience Type
                    </p>
                    <div className="flex items-center gap-2">
                      {audienceType === 1 ? (
                        <>
                          <Users className="w-4 h-4 text-blue-600" />
                          <p className="font-medium text-gray-900">Customers</p>
                        </>
                      ) : (
                        <>
                          <Briefcase className="w-4 h-4 text-purple-600" />
                          <p className="font-medium text-gray-900">
                            Business Users
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  {formData.segmentName && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                        Segment Name
                      </p>
                      <p className="font-medium text-gray-900">
                        {formData.segmentName}
                      </p>
                    </div>
                  )}

                  {formData.description && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                        Description
                      </p>
                      <p className="text-sm text-gray-700 line-clamp-3">
                        {formData.description}
                      </p>
                    </div>
                  )}

                  <div className="pt-4 border-t-2 border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                      Active Filters
                    </p>
                    {audienceType === 1 ? (
                      getActiveFilterCount() > 0 ? (
                        <div className="space-y-2">
                          {filterCategories.map((category) => {
                            const activeFilters = getActiveFiltersForCategory(
                              category.prefix
                            );
                            if (activeFilters.length === 0) return null;
                            const colors = colorClasses[category.color];

                            return activeFilters.map((fieldName) => (
                              <div
                                key={fieldName}
                                className={`flex items-center justify-between gap-2 p-2 rounded-lg ${colors.bg}`}
                              >
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <div
                                    className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${colors.text.replace(
                                      "text-",
                                      "bg-"
                                    )}`}
                                  />
                                  <span className="text-xs font-medium text-gray-700 truncate">
                                    {getFieldLabel(fieldName)}
                                  </span>
                                </div>
                                <button
                                  onClick={() => removeFilterField(fieldName)}
                                  className="p-1 hover:bg-white rounded transition-colors flex-shrink-0"
                                >
                                  <X className="w-3 h-3 text-gray-500" />
                                </button>
                              </div>
                            ));
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">
                          No filters applied
                        </p>
                      )
                    ) : (
                      <div className="space-y-2 text-sm">
                        {formData.department && (
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-purple-600" />
                            <span className="text-gray-700">
                              Department:{" "}
                              <span className="font-medium">
                                {departmentOptions.find(
                                  (opt) => opt.value === formData.department
                                )?.label || formData.department}
                              </span>
                            </span>
                          </div>
                        )}
                        {formData.role && (
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-purple-600" />
                            <span className="text-gray-700">
                              Role:{" "}
                              <span className="font-medium">
                                {roleOptions.find(
                                  (opt) => opt.value == formData.role
                                )?.label || formData.role}
                              </span>
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-purple-600" />
                          <span className="text-gray-700">
                            Status:{" "}
                            <span className="font-medium">
                              {formData.accountStatus === "1"
                                ? "All User"
                                : formData.accountStatus === "2"
                                ? "Active User Only"
                                : formData.accountStatus === "3"
                                ? "Inactive User Only"
                                : "All User"}
                            </span>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border-2 border-blue-200 p-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                    <Info className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">
                      Quick Tips
                    </h4>
                    <ul className="text-sm text-gray-700 space-y-1.5">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>
                          Combine multiple filters for precise targeting
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>
                          Segments update automatically as data changes
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span>
                          Use descriptive names for easy identification
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
